import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";
import { useFavorites } from "@/hooks/useFavorites";

interface MediaDetailProps {
  item: MediaItem;
  related: MediaItem[];
}

export default function MediaDetail({ item, related }: MediaDetailProps) {
  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState<string | null>(null);
  const favorited = isFavorite(item.id);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      // ignore clipboard errors
    }
    setToast("分享链接已复制，快分享给好友吧");
  };

  const handleFavorite = () => {
    toggleFavorite(item.id);
    setToast(favorited ? "已取消收藏" : "已加入收藏，可在本地保存");
  };

  return (
    <div className="relative">
      {/* Hero */}
      <div className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} ${item.genres.join(" ")}`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/80 to-primary-950/55" />
        </div>
        <div className="relative w-full px-4 md:px-6 pt-8 pb-8 md:pt-12 md:pb-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-end">
            <div className="w-32 shrink-0 overflow-hidden rounded-lg ring-1 ring-white/20 md:w-52">
              <div className="w-full aspect-[2/3]">
                <img
                  src={item.poster}
                  alt={`${item.title} 海报`}
                  title={`${item.title} 影视海报`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-foreground-950">
                  {item.type}
                </span>
                <span className="rounded-md bg-white/15 px-2.5 py-1 text-xs text-white backdrop-blur">
                  {item.year}
                </span>
                <span className="rounded-md bg-white/15 px-2.5 py-1 text-xs text-white backdrop-blur">
                  {item.region}
                </span>
              </div>
              <h1 className="mt-3 text-3xl font-bold text-white font-heading md:text-5xl">
                {item.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-white/85 md:text-base">
                {item.synopsis}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
                <span className="flex items-center gap-1.5 font-label text-base font-semibold text-accent-400">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-clapperboard-line"></i>
                  导演：{item.director}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {item.meta}
                </span>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-medium transition-colors cursor-pointer ${
                    favorited
                      ? "bg-accent-500 text-foreground-950 hover:bg-accent-400"
                      : "bg-primary-600 text-background-50 hover:bg-primary-700"
                  }`}
                >
                  <i className={favorited ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {favorited ? "已收藏" : "加入收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-white/15 px-5 py-2.5 text-sm font-medium text-white ring-1 ring-white/30 backdrop-blur hover:bg-white/25 transition-colors cursor-pointer"
                >
                  <i className="ri-share-line"></i>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 mt-8">
        <div className="lg:flex lg:gap-8">
          <div className="min-w-0 flex-1">
            <section className="rounded-lg bg-background-100 p-5 ring-1 ring-background-200 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">
                剧情简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">
                {item.synopsis}
              </p>
            </section>

            <section className="mt-6 rounded-lg bg-background-100 p-5 ring-1 ring-background-200 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">
                详细介绍
              </h2>
              <div className="mt-3 space-y-4">
                {item.description.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-sm leading-loose text-foreground-700 md:text-base"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            <section className="mt-6 rounded-lg bg-background-100 p-5 ring-1 ring-background-200 md:p-6">
              <h2 className="text-lg font-bold text-foreground-950 font-heading">
                精彩剧照
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="w-full aspect-[16/10] overflow-hidden rounded-md">
                  <img
                    src={item.backdrop}
                    alt={`${item.title} 剧照一`}
                    title={`${item.title} 剧照`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="w-full aspect-[16/10] overflow-hidden rounded-md">
                  <img
                    src={item.poster}
                    alt={`${item.title} 海报剧照`}
                    title={`${item.title} 海报`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </section>
          </div>

          <aside className="mt-6 w-full lg:mt-0 lg:w-80 lg:shrink-0">
            <section className="rounded-lg bg-background-100 p-5 ring-1 ring-background-200">
              <h2 className="text-sm font-bold text-foreground-950 font-heading">
                所属分类
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  to={`/category/${item.type}`}
                  className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {item.type}
                </Link>
                {item.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/search?q=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
              <dl className="mt-5 space-y-3 border-t border-background-200 pt-4 text-sm">
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-foreground-400">导演</dt>
                  <dd className="text-foreground-800">{item.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-foreground-400">主演</dt>
                  <dd className="text-foreground-800">{item.actors.join(" / ")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-foreground-400">地区</dt>
                  <dd className="text-foreground-800">{item.region}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-14 shrink-0 text-foreground-400">上映</dt>
                  <dd className="text-foreground-800">{item.year} 年</dd>
                </div>
              </dl>
            </section>

            <section className="mt-6 rounded-lg bg-background-100 p-5 ring-1 ring-background-200">
              <h2 className="text-sm font-bold text-foreground-950 font-heading">
                相关推荐
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/detail/${rel.id}`}
                    className="group flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-14 h-20 shrink-0 overflow-hidden rounded-md">
                      <img
                        src={rel.poster}
                        alt={rel.title}
                        title={`${rel.title} 推荐`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {rel.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-400 line-clamp-1">
                        {rel.genres.join(" · ")}
                      </p>
                      <span className="mt-1 inline-flex items-center gap-1 font-label text-xs font-semibold text-accent-600">
                        <i className="ri-star-fill"></i>
                        {rel.rating.toFixed(1)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>

      {toast ? (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950/90 px-5 py-2.5 text-sm text-background-50 animate-fade-in">
          {toast}
        </div>
      ) : null}
    </div>
  );
}